import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { stampOrgId } from '@/lib/telemetryOrgContext';
import { buildErrorFingerprint, isClientHttpError } from '@/services/errorFingerprint';

/**
 * Centralized logging service that handles both console logging (development)
 * and Application Insights telemetry (production)
 */
class Logger {
  private appInsights: ApplicationInsights | null = null;
  private isDevelopment = import.meta.env.DEV;
  private isEnabled = false;
  private orgId: string | null = null;

  /**
   * Initialize the logger with Application Insights instance.
   * Registers a telemetry initializer that stamps the selected org onto every item, including
   * auto-collected exceptions, so browser errors can be ranked by breadth in error triage.
   */
  initialize(appInsights: ApplicationInsights | null): void {
    this.appInsights = appInsights;
    this.isEnabled = true;
    appInsights?.addTelemetryInitializer((item) => {
      stampOrgId(item, this.orgId);
    });
  }

  /**
   * Set the organization stamped onto all subsequent telemetry.
   * Safe to call before initialize; the value is read when each item is sent.
   */
  setOrg(orgId: string | null): void {
    this.orgId = orgId;
  }

  /**
   * Log debug-level messages (development only)
   */
  debug(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[DEBUG] ${message}`, properties ?? '');
    }
    // Don't send debug logs to Application Insights
  }

  /**
   * Log info-level messages
   */
  info(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, properties ?? '');
    }

    if (this.appInsights && this.isEnabled) {
      this.appInsights.trackTrace({
        message,
        severityLevel: SeverityLevel.Information,
        properties,
      });
    }
  }

  /**
   * Log warning-level messages
   */
  warn(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, properties ?? '');
    }

    if (this.appInsights && this.isEnabled) {
      this.appInsights.trackTrace({
        message,
        severityLevel: SeverityLevel.Warning,
        properties,
      });
    }
  }

  /**
   * Log error-level messages.
   * Attaches a stable `fingerprint` (built from `properties.context` when it is a string) that the
   * error-triage feeder groups on. Handled HTTP 4xx responses are not defects, so they are sent as
   * warning traces instead, which the feeder ignores.
   */
  error(error: Error | string, properties?: Record<string, unknown>): void {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const context = typeof properties?.context === 'string' ? properties.context : undefined;
    const fingerprint = buildErrorFingerprint(errorObj, context);

    if (isClientHttpError(errorObj)) {
      this.warn(errorObj.message, { ...properties, fingerprint });
      return;
    }

    if (this.isDevelopment) {
      console.error(`[ERROR] ${errorObj.message}`, errorObj, properties ?? '');
    }

    if (this.appInsights && this.isEnabled) {
      this.appInsights.trackException({
        exception: errorObj,
        severityLevel: SeverityLevel.Error,
        properties: { ...properties, fingerprint },
      });
    }
  }

  /**
   * Track custom events (user actions, business events, etc.)
   */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[EVENT] ${name}`, properties ?? '');
    }

    if (this.appInsights && this.isEnabled) {
      this.appInsights.trackEvent({ name, properties });
    }
  }

  /**
   * Track page views manually (automatic tracking is enabled by default)
   */
  trackPageView(name: string, uri?: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[PAGE_VIEW] ${name}`, uri, properties ?? '');
    }

    if (this.appInsights && this.isEnabled) {
      this.appInsights.trackPageView({
        name,
        uri,
        properties,
      });
    }
  }

  /**
   * Set user context for all subsequent telemetry
   */
  setUser(userId: string, accountId?: string): void {
    if (this.appInsights && this.isEnabled) {
      this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
    }
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    if (this.appInsights && this.isEnabled) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }
}

/**
 * Singleton logger instance
 * Import this to use logging throughout the application
 */
export const logger = new Logger();
